import React, { useState, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";

const SelectableListView = observer(
  ({
    data = [],
    isLoading,
    columns = [],
    renderGridItem,
    onRowClick,
    columnVisibilityKey,
    defaultVisibleColumnKeys,
    enableSort = true,
    sortColumn: externalSortColumn,
    sortDirection: externalSortDirection,
    onSortChange,
    enablePagination = true,
    itemsPerPage = 30,
    totalItems = 0,
    currentPage = 1,
    onPageChange,
  }) => {
    const { isTableView, isAdvancedTable, viewMode } = useTableView();
    
    // Internal sort state (used when onSortChange is not provided)
    const [internalSortColumn, setInternalSortColumn] = useState(null);
    const [internalSortDirection, setInternalSortDirection] = useState("asc");
    
    // Use external sort state if provided, otherwise use internal
    const sortColumn = externalSortColumn !== undefined ? externalSortColumn : internalSortColumn;
    const sortDirection = externalSortDirection !== undefined ? externalSortDirection : internalSortDirection;

    const allColumnKeys = useMemo(
      () => (columns ? columns.map((column) => column.key) : []),
      [columns]
    );
    const columnVisibilityEnabled = Boolean(columnVisibilityKey);

    const getInitialVisibleColumnKeys = () => {
      if (!columnVisibilityEnabled) {
        return allColumnKeys;
      }

      if (typeof window !== "undefined") {
        const storedValue = window.localStorage.getItem(columnVisibilityKey);
        if (storedValue) {
          try {
            const parsed = JSON.parse(storedValue);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const sanitized = parsed.filter((key) =>
                allColumnKeys.includes(key)
              );
              if (sanitized.length > 0) {
                return sanitized;
              }
            }
          } catch (error) {
            console.error("Failed to parse stored column preferences", error);
          }
        }
      }

      if (
        Array.isArray(defaultVisibleColumnKeys) &&
        defaultVisibleColumnKeys.length > 0
      ) {
        const sanitizedDefaults = defaultVisibleColumnKeys.filter((key) =>
          allColumnKeys.includes(key)
        );
        if (sanitizedDefaults.length > 0) {
          return sanitizedDefaults;
        }
      }

      return allColumnKeys;
    };

    const [visibleColumnKeys, setVisibleColumnKeys] = useState(
      getInitialVisibleColumnKeys
    );
    const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
    const columnSelectorRef = useRef(null);

    useEffect(() => {
      if (!columnVisibilityEnabled) {
        setVisibleColumnKeys(allColumnKeys);
        return;
      }

      setVisibleColumnKeys((prevKeys) => {
        const sanitizedPrev = prevKeys.filter((key) =>
          allColumnKeys.includes(key)
        );
        return sanitizedPrev.length > 0 ? sanitizedPrev : allColumnKeys;
      });
    }, [allColumnKeys, columnVisibilityEnabled]);

    useEffect(() => {
      if (!isColumnSelectorOpen) {
        return;
      }

      const handleClickOutside = (event) => {
        if (
          columnSelectorRef.current &&
          !columnSelectorRef.current.contains(event.target)
        ) {
          setIsColumnSelectorOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isColumnSelectorOpen]);

    useEffect(() => {
      if (!columnVisibilityEnabled || typeof window === "undefined") {
        return;
      }

      window.localStorage.setItem(
        columnVisibilityKey,
        JSON.stringify(visibleColumnKeys)
      );
    }, [columnVisibilityEnabled, columnVisibilityKey, visibleColumnKeys]);

    const toggleColumnVisibility = (columnKey, checked) => {
      if (!columnVisibilityEnabled) {
        return;
      }

      setVisibleColumnKeys((prevKeys) => {
        if (checked) {
          if (!prevKeys.includes(columnKey)) {
            return [...prevKeys, columnKey];
          }
          return prevKeys;
        } else {
          if (prevKeys.length === 1) {
            return prevKeys;
          }
          return prevKeys.filter((key) => key !== columnKey);
        }
      });
    };

    const handleSelectAllColumns = () => {
      if (!columnVisibilityEnabled) {
        return;
      }
      setVisibleColumnKeys(allColumnKeys);
    };

    const handleResetColumns = () => {
      if (!columnVisibilityEnabled) {
        return;
      }
      if (
        Array.isArray(defaultVisibleColumnKeys) &&
        defaultVisibleColumnKeys.length > 0
      ) {
        const sanitizedDefaults = defaultVisibleColumnKeys.filter((key) =>
          allColumnKeys.includes(key)
        );
        if (sanitizedDefaults.length > 0) {
          setVisibleColumnKeys(sanitizedDefaults);
          return;
        }
      }
      setVisibleColumnKeys(allColumnKeys);
    };

    const displayedColumns = useMemo(() => {
      if (!columnVisibilityEnabled) {
        return columns;
      }
      return columns.filter((column) => visibleColumnKeys.includes(column.key));
    }, [columnVisibilityEnabled, columns, visibleColumnKeys]);

    const handleSort = (columnKey) => {
      if (!enableSort) return;
      
      const column = columns.find((col) => col.key === columnKey);
      if (column?.sortable === false) return;
      
      let newDirection = "asc";
      if (sortColumn === columnKey) {
        newDirection = sortDirection === "asc" ? "desc" : "asc";
      }
      
      if (onSortChange) {
        // External control - call the callback for server-side sorting
        onSortChange(columnKey, newDirection);
      } else {
        // Internal control
        setInternalSortColumn(columnKey);
        setInternalSortDirection(newDirection);
      }
    };

    // Convert columns to TanStack Table format
    const tableColumns = useMemo(() => {
      return displayedColumns.map((column) => ({
        id: column.key,
        accessorKey: column.key,
        header: () => {
          const isSortable = enableSort && column.sortable !== false;
          const isCurrentSort = sortColumn === column.key;
          
          return (
            <div
              className={`flex items-center gap-1 ${isSortable ? "cursor-pointer select-none" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (isSortable) handleSort(column.key);
              }}
            >
              {column.label}
              {isSortable && (
                <span className="ml-1">
                  {isCurrentSort ? (
                    sortDirection === "asc" ? (
                      <ArrowUp className="h-4 w-4" />
                    ) : (
                      <ArrowDown className="h-4 w-4" />
                    )
                  ) : (
                    <ArrowUpDown className="h-4 w-4 opacity-30" />
                  )}
                </span>
              )}
            </div>
          );
        },
        cell: ({ row }) => {
          const item = row.original;
          if (column.render) {
            return column.render(item);
          }
          if (column.accessor) {
            return column.accessor(item);
          }
          return item[column.key];
        },
      }));
    }, [displayedColumns, enableSort, sortColumn, sortDirection]);

    const table = useReactTable({
      data,
      columns: tableColumns,
      getCoreRowModel: getCoreRowModel(),
    });

    const pageCount = Math.ceil(totalItems / itemsPerPage);

    if (isLoading) return <div>Loading...</div>;

    // Show column visibility when columnVisibilityKey is provided
    const showColumnVisibility = columnVisibilityEnabled && columns.length > 0;

    return (
      <div className="flex flex-col h-full">
        {isTableView || !renderGridItem ? (
          <>
            {/* Column Visibility Controls - Only in Advanced Table mode */}
            {showColumnVisibility && (
              <div className="flex justify-end items-center mb-4 flex-shrink-0">
                <div className="relative" ref={columnSelectorRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setIsColumnSelectorOpen((prevState) => !prevState)
                    }
                  >
                    Columns
                  </Button>
                  {isColumnSelectorOpen && (
                    <div className="absolute right-0 z-20 mt-2 w-64 rounded-md border bg-card shadow-lg">
                      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
                        <span className="text-sm font-medium">
                          Visible Columns
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleResetColumns}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleSelectAllColumns}
                          >
                            All
                          </Button>
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto px-3 py-2">
                        {columns.map((column) => (
                          <div
                            key={column.key}
                            className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-muted rounded px-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleColumnVisibility(
                                column.key,
                                !visibleColumnKeys.includes(column.key)
                              );
                            }}
                          >
                            <Checkbox
                              checked={visibleColumnKeys.includes(column.key)}
                              onCheckedChange={(checked) =>
                                toggleColumnVisibility(column.key, checked)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="truncate select-none" title={column.label}>
                              {column.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Table Container with Scrollable Body and Sticky Header */}
            <div className="flex-1 rounded-md border overflow-auto relative">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-muted">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, idx) => (
                        <th
                          key={header.id}
                          className={`text-muted-foreground font-medium border-b border-r border-border p-2 text-left bg-muted whitespace-nowrap ${
                            idx === headerGroup.headers.length - 1
                              ? "border-r-0"
                              : ""
                          }`}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row, rowIndex) => (
                    <tr
                      key={row.id}
                      className={[
                        rowIndex % 2 === 0 ? "bg-card" : "bg-muted/50",
                        onRowClick ? "cursor-pointer hover:bg-primary/10" : "",
                      ].join(" ")}
                      onClick={() => onRowClick && onRowClick(row.original)}
                    >
                      {row.getVisibleCells().map((cell, idx) => (
                        <td
                          key={cell.id}
                          className={`border-b border-r border-border p-2 whitespace-nowrap ${
                            idx === row.getVisibleCells().length - 1
                              ? "border-r-0"
                              : ""
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {enablePagination && (
              <div className="flex justify-end flex-shrink-0 mt-4">
                <div className="flex items-center space-x-2 py-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>
                    {currentPage} / {pageCount}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === pageCount}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(pageCount)}
                    disabled={currentPage === pageCount}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center pl-4 space-x-2 py-4">
                  Total: {totalItems}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.map(
              (item, index) =>
                renderGridItem && (
                  <div key={index}>
                    {renderGridItem(item)}
                  </div>
                )
            )}
          </div>
        )}
      </div>
    );
  }
);

export default SelectableListView;
